import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LogoPicker from "@/components/LogoPicker";
import LogoDisplay from "@/components/LogoDisplay";
import { Upload, Trash2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const NONE_CATEGORY = "__none__";

export default function LinkFormDialog({ open, onOpenChange, link, categories, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    url: "",
    description: "",
    category_id: NONE_CATEGORY,
    logo_type: "icon",
    logo_value: "globe",
    cred_login: "",
    cred_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open) {
      if (link) {
        setForm({
          title: link.title || "",
          url: link.url || "",
          description: link.description || "",
          category_id: link.category_id || NONE_CATEGORY,
          logo_type: link.logo_type || "icon",
          logo_value: link.logo_value || "globe",
          cred_login: link.cred_login || "",
          cred_password: link.cred_password || "",
        });
      } else {
        setForm({
          title: "",
          url: "",
          description: "",
          category_id: NONE_CATEGORY,
          logo_type: "icon",
          logo_value: "globe",
          cred_login: "",
          cred_password: "",
        });
      }
      setShowPassword(false);
    }
  }, [open, link]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_type: "upload", logo_value: data.path }));
      toast.success("Logo téléchargé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      url: form.url.trim(),
      description: form.description.trim(),
      logo_type: form.logo_type,
      logo_value: form.logo_value,
      category_id: form.category_id === NONE_CATEGORY ? null : form.category_id,
      cred_login: form.cred_login.trim(),
      cred_password: form.cred_password,
    };
    try {
      if (link) {
        await api.patch(`/admin/links/${link.id}`, payload);
        toast.success("Lien modifié");
      } else {
        await api.post("/admin/links", payload);
        toast.success("Lien créé");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:w-full max-w-2xl max-h-[92vh] p-0 gap-0 flex flex-col"
        data-testid="link-form-dialog"
      >
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-border shrink-0">
            <DialogTitle>{link ? "Modifier le lien" : "Nouveau lien"}</DialogTitle>
            <DialogDescription>
              Renseignez le titre, l'URL et choisissez un logo pour ce lien.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-4 min-h-0">
            <div className="flex items-center gap-4 p-4 border border-border rounded-sm bg-accent/30">
              <LogoDisplay type={form.logo_type} value={form.logo_value} size={48} />
              <div className="text-sm">
                <p className="font-medium">Aperçu du logo</p>
                <p className="text-xs text-muted-foreground">
                  Source : {form.logo_type === "upload" ? "fichier téléchargé" : form.logo_type === "url" ? "URL externe" : "bibliothèque"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Titre</Label>
              <Input
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="mt-2"
                data-testid="link-form-title"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">URL</Label>
              <Input
                required
                type="text"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://… ou rdp://serveur, mailto:x@y.fr, ms-teams:/l/…"
                className="mt-2"
                data-testid="link-form-url"
              />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground -mt-2 px-1">
            💡 Astuce : pour lancer une <strong>app Windows/Mac</strong> au clic, utilisez un schéma de protocole : <code>rdp://serveur</code>, <code>ms-teams:/l/chat/0/0?users=alice@…</code>, <code>vscode://file/c:/projet</code>, <code>mailto:alice@…</code>, <code>tel:+33612…</code>, <code>slack://channel?team=T&id=C</code>, <code>vnc://…</code>, etc. Toute URL custom <code>monapp://...</code> est acceptée.
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Description (optionnel)</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="mt-2"
              data-testid="link-form-description"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Catégorie</Label>
            <Select
              value={form.category_id}
              onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
            >
              <SelectTrigger className="mt-2" data-testid="link-form-category-trigger">
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_CATEGORY}>Aucune</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.parent_id ? "↳ " : ""}{c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t border-border pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs uppercase tracking-wider mb-0">Credentials (optionnel)</Label>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">
              Au clic sur le lien dans le portail, les identifiants seront copiés dans le presse-papier puis le site cible ouvert dans un nouvel onglet. Stockés chiffrés AES-256-GCM.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Login</Label>
                <Input
                  value={form.cred_login}
                  onChange={(e) => setForm((f) => ({ ...f, cred_login: e.target.value }))}
                  placeholder="admin, user@example.com…"
                  className="mt-1.5"
                  data-testid="link-form-cred-login"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-xs">Mot de passe</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={form.cred_password}
                    onChange={(e) => setForm((f) => ({ ...f, cred_password: e.target.value }))}
                    placeholder="Mot de passe"
                    className="pr-10"
                    data-testid="link-form-cred-password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Logo</Label>

            {/* Aperçu + retrait du logo actuel */}
            {form.logo_value && (
              <div className="mt-2 flex items-center gap-3 p-3 border border-border rounded-sm bg-muted/30" data-testid="logo-current-preview">
                <LogoDisplay
                  type={form.logo_type}
                  value={form.logo_value}
                  size={40}
                  alt="Logo actuel"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Logo actuel</p>
                  <p className="text-xs font-mono truncate">{form.logo_value}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, logo_type: "icon", logo_value: "globe" }))}
                  className="h-8 px-3 inline-flex items-center gap-1.5 rounded-sm border border-border text-xs font-medium hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  data-testid="logo-remove-button"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Retirer
                </button>
              </div>
            )}

            <Tabs
              value={form.logo_type}
              onValueChange={(v) => setForm((f) => ({ ...f, logo_type: v, logo_value: v === "icon" ? "globe" : "" }))}
              className="mt-2"
            >
              <TabsList className="grid grid-cols-3" data-testid="link-form-logo-tabs">
                <TabsTrigger value="icon" data-testid="logo-tab-icon">Bibliothèque</TabsTrigger>
                <TabsTrigger value="upload" data-testid="logo-tab-upload">Téléchargement</TabsTrigger>
                <TabsTrigger value="url" data-testid="logo-tab-url">URL</TabsTrigger>
              </TabsList>
              <TabsContent value="icon" className="mt-4">
                <LogoPicker
                  value={form.logo_value}
                  onChange={(key) => setForm((f) => ({ ...f, logo_value: key }))}
                />
              </TabsContent>
              <TabsContent value="upload" className="mt-4 space-y-3">
                <label className="flex items-center justify-center gap-2 h-24 border border-dashed border-border rounded-sm cursor-pointer hover:bg-accent transition-colors" data-testid="logo-upload-zone">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {uploading ? "Téléchargement…" : "Cliquez pour téléverser un logo (png, jpg, svg, max 5 Mo)"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                    data-testid="logo-upload-input"
                  />
                </label>
                {form.logo_type === "upload" && form.logo_value && (
                  <p className="text-xs text-muted-foreground">Fichier : {form.logo_value}</p>
                )}
              </TabsContent>
              <TabsContent value="url" className="mt-4">
                <Input
                  placeholder="https://example.com/logo.png"
                  value={form.logo_value}
                  onChange={(e) => setForm((f) => ({ ...f, logo_value: e.target.value }))}
                  data-testid="logo-url-input"
                />
              </TabsContent>
            </Tabs>
          </div>
          </div>

          <div className="px-5 sm:px-6 py-3 sm:py-4 border-t border-border bg-background shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-11 sm:h-10 px-4 rounded-sm border border-border text-sm font-medium hover:bg-accent"
              data-testid="link-form-cancel"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="h-11 sm:h-10 px-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium disabled:opacity-60"
              data-testid="link-form-submit"
            >
              {saving ? "Enregistrement…" : link ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
